import { forwardRef, type AnchorHTMLAttributes } from "react";
import { Link as RouterLink, useNavigate, useLocation, useParams } from "react-router-dom";
import { DEFAULT_LOCALE, SUPPORTED_LOCALES, type Locale } from "@/i18n";

/**
 * Drop-in for the Next app's `@/i18n/navigation` (next-intl createNavigation).
 * Exposes the SAME surface — { Link, useRouter, usePathname, redirect } — with
 * the SAME `href`-based API, so app pages port over verbatim. Locale comes from
 * the URL path (/:locale/...) and is auto-prefixed onto in-app hrefs.
 */

function useActiveLocale(): Locale {
  const { locale } = useParams();
  return (SUPPORTED_LOCALES.includes(locale as Locale) ? locale : DEFAULT_LOCALE) as Locale;
}

/** Prefix an absolute in-app path with the active locale ("/x" -> "/en/x"). */
function localize(href: string, lng: Locale): string {
  if (!href.startsWith("/") || href.startsWith("//")) return href;
  const first = href.split("/")[1];
  if (SUPPORTED_LOCALES.includes(first as Locale)) return href;
  return `/${lng}${href === "/" ? "" : href}`;
}

/**
 * The app-surface path prefixes the SPA serves client-side (post-P3-cutover,
 * nginx routes exactly these to the static SPA; everything else — marketing,
 * guides, legal — stays on headless Next for SEO). Kept in sync with the router
 * tree and the P3 nginx location regex (SPA-P3-CUTOVER.md). A link OUT of the
 * app surface must be a full-page navigation so Next serves it, not a
 * client-side route that would 404 in the SPA.
 */
export const APP_PATH_PREFIXES = new Set([
  "portal", "admin", "tools", "status", "checkout", "my-reports",
  "investigate", "dashboard", "explore", "report", "filing",
]);

/** True if `localized` (/:locale/<prefix>/...) is served client-side by the SPA. */
function isAppPath(localized: string): boolean {
  if (!localized.startsWith("/")) return false; // external / mailto: / #hash
  const segs = localized.split("/").filter(Boolean); // [locale, prefix, ...]
  return segs.length >= 2 && APP_PATH_PREFIXES.has(segs[1]);
}

type LinkProps = Omit<AnchorHTMLAttributes<HTMLAnchorElement>, "href"> & {
  href: string;
  locale?: Locale;
};

export const Link = forwardRef<HTMLAnchorElement, LinkProps>(function Link(
  { href, locale, ...rest },
  ref,
) {
  const active = useActiveLocale();
  const to = localize(href, locale ?? active);
  // App-surface paths route client-side; marketing/guides/legal (and external
  // / mailto: / #hash) are full-page <a> navigations so headless Next serves
  // them. Without this, a shell link to /pricing would client-route into the
  // SPA and 404.
  if (!isAppPath(to)) {
    return <a ref={ref} href={to} {...rest} />;
  }
  return <RouterLink ref={ref} to={to} {...rest} />;
});

/** next-intl-shaped router: push/replace accept locale-aware hrefs. */
export function useRouter() {
  const navigate = useNavigate();
  const active = useActiveLocale();
  return {
    // Second arg mirrors Next's `router.push(href, { scroll })`; the SPA has no
    // ScrollRestoration wired, so `{ scroll: false }` is already the default —
    // accept and ignore it so ported callsites (dashboard filters) typecheck.
    push: (href: string, _opts?: { scroll?: boolean }) => navigate(localize(href, active)),
    replace: (href: string, _opts?: { scroll?: boolean }) =>
      navigate(localize(href, active), { replace: true }),
    back: () => navigate(-1),
    forward: () => navigate(1),
    refresh: () => navigate(0),
    prefetch: () => {},
  };
}

/** next-intl usePathname() returns the path WITHOUT the locale prefix. */
export function usePathname(): string {
  const { pathname } = useLocation();
  const segs = pathname.split("/");
  if (SUPPORTED_LOCALES.includes(segs[1] as Locale)) {
    const rest = "/" + segs.slice(2).join("/");
    return rest === "/" ? "/" : rest.replace(/\/$/, "");
  }
  return pathname;
}

/** Best-effort client redirect (full navigation, locale-prefixed). */
export function redirect(href: string): never {
  const lng = (window.location.pathname.split("/")[1] || DEFAULT_LOCALE) as Locale;
  window.location.assign(localize(href, SUPPORTED_LOCALES.includes(lng) ? lng : DEFAULT_LOCALE));
  throw new Error("redirect");
}

export default Link;
